import { useEffect, useRef, useState } from "react";
import AuthLayout from "@container/layout/auth";
import { Button } from "@component/button";
import { EmptyState } from "@component/empty-state";
import { useUserProfileContext } from "@context/user_profile";
import { getAdminCompanyList, getAdminCompanyJobs } from "@service/admin";
import {
    getMayaSimulateThread,
    simulateMayaChat,
    simulateMayaUpload,
    type SimulateFlowState,
} from "@service/maya-simulator";
import {
    firstImage,
    getReceptionistSimulateThread,
    isSimulatorUnavailable,
    simulateReceptionistChat,
    simulateReceptionistUpload,
    type ReceptionistReplyItem,
} from "@service/receptionist-simulator";
import { mergeThreadRows, type SimMessage } from "./threadMerge";
import { listMerchants } from "@service/receptionist";
import { isReceptionistEnabled } from "@receptionist/role";
import { catchErrorLog } from "@util/sentry";
import { cn } from "@util/tailwind";
import {
    AlertTriangleIcon,
    BotIcon,
    BriefcaseIcon,
    DownloadIcon,
    FileTextIcon,
    Loader2Icon,
    MessageSquareIcon,
    PaperclipIcon,
    RotateCcwIcon,
    SendIcon,
    StoreIcon,
    XIcon,
} from "lucide-react";


// Accepted upload types + size limit (mirrors BE ourteam-backend#3723).
const UPLOAD_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
const UPLOAD_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

/** Human-readable file size, e.g. "2.4 MB". */
function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Whitelisted tester WhatsApp numbers for the simulator. A custom number is
 *  allowed too, but anything outside this list warns the tester to double-check. */
const WHITELIST_PHONES: { label: string; phone: string }[] = [
    { label: "Yewwai", phone: "+60173732750" },
    { label: "Jovi", phone: "+60163190603" },
];

/** `eligibility_check` → `Eligibility Check`. */
function formatStep(step: string): string {
    return step.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtTime(d: Date): string {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

/** `2026-07-25 10:23` — for the transcript header. */
function fmtDateTime(d: Date): string {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(
        d.getHours()
    )}:${pad2(d.getMinutes())}`;
}

/** `20260725-1023` — for the transcript filename. */
function fmtStamp(d: Date): string {
    return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(
        d.getHours()
    )}${pad2(d.getMinutes())}`;
}

function slugify(s: string): string {
    return (
        s
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 40) || "job"
    );
}

// Persist the simulator session across refresh. Uses localStorage (not a cookie)
// because the transcript can exceed the ~4KB cookie limit and shouldn't ride on
// every request. Cleared only when the admin starts a new candidate.
const STORAGE_KEYS: Record<SimMode, string> = {
    // Unchanged for the recruiter tab, so a session in flight survives this change.
    recruiter: "__maya_simulator_state_v1__",
    receptionist: "__receptionist_simulator_state_v1__",
};

/**
 * Which assistant is being driven. The two tabs are the same simulator — same
 * thread, same composer, same transcript — pointed at different runtimes: the
 * recruitment webhook, and the receptionist one that rides beside it
 * (ourteam-backend#4666). Everything that differs is gathered here rather than
 * forked into a second copy of the screen.
 */
export type SimMode = "recruiter" | "receptionist";

interface PersistedSim {
    selectedCompanyId: string;
    selectedJobId: string;
    /** Receptionist tab only. */
    selectedMerchantId?: string;
    profileName: string;
    phoneChoice: string;
    customPhone: string;
    /** Messages with `at` serialized to ISO (revived to Date on load). */
    messages: (Omit<SimMessage, "at"> & { at: string })[];
    flow?: SimulateFlowState;
    candidateId: string;
    idCounter: number;
}

function loadPersistedSim(key: string): PersistedSim | null {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as PersistedSim) : null;
    } catch {
        return null;
    }
}

/** `senderName` is who the reply is from: Maya on the recruiter tab, the
 *  merchant's own receptionist on the other. Labelling every reply "Maya" was
 *  wrong on the receptionist tab — that assistant is not Maya, and the name a
 *  tester reads should be the one the customer would see. */
function ChatBubble({ msg, senderName }: { msg: SimMessage; senderName: string }) {
    const isCandidate = msg.sender === "candidate";
    return (
        <div className={cn("flex", isCandidate ? "justify-end" : "justify-start")}>
            <div
                className={cn(
                    "max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm whitespace-pre-wrap break-words",
                    isCandidate
                        ? "bg-[#d9fdd3] text-gray-900 rounded-br-sm"
                        : "bg-white text-gray-900 rounded-bl-sm border border-gray-100"
                )}
            >
                {!isCandidate && (
                    <div className="text-[11px] font-semibold text-[#075e54] mb-0.5">{senderName}</div>
                )}
                {msg.file && (
                    <div className="mb-1 flex items-center gap-2 rounded-lg bg-black/5 px-2 py-1.5">
                        <FileTextIcon className="w-4 h-4 flex-shrink-0 text-gray-600" />
                        <span className="text-xs font-medium text-gray-800 break-all">
                            {msg.file.name}
                        </span>
                    </div>
                )}
                {msg.imageUrl && (
                    <a
                        href={msg.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn("block -mx-1", msg.body && "mb-1")}
                        title="Open the image full size"
                    >
                        <img
                            src={msg.imageUrl}
                            alt={msg.body || "Image"}
                            loading="lazy"
                            className="max-h-60 w-auto max-w-full rounded-lg object-cover"
                        />
                    </a>
                )}
                {msg.body && <span>{msg.body}</span>}
                <div
                    className={cn(
                        "text-[10px] mt-1 text-right",
                        isCandidate ? "text-gray-500" : "text-gray-400"
                    )}
                >
                    {fmtTime(msg.at)}
                </div>
            </div>
        </div>
    );
}

function TypingIndicator() {
    return (
        <div className="flex justify-start">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-3.5 py-2.5 shadow-sm">
                <div className="flex items-center gap-1">
                    <span
                        className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                        style={{ animationDelay: "0ms" }}
                    />
                    <span
                        className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                        style={{ animationDelay: "150ms" }}
                    />
                    <span
                        className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                        style={{ animationDelay: "300ms" }}
                    />
                    <span className="ml-1.5 text-[11px] text-gray-400">Maya is typing…</span>
                </div>
            </div>
        </div>
    );
}

function SimulatorPanel({ mode }: { mode: SimMode }) {
    const [, userInfo] = useUserProfileContext();
    const isRcp = mode === "receptionist";
    const storageKey = STORAGE_KEYS[mode];

    // Restore a persisted session once (survives refresh until "New candidate").
    const [persisted] = useState(() => loadPersistedSim(storageKey));

    const [merchants, setMerchants] = useState<{ id: string; name: string }[]>([]);
    const [merchantsLoading, setMerchantsLoading] = useState<boolean>(isRcp);
    const [selectedMerchantId, setSelectedMerchantId] = useState<string>(
        persisted?.selectedMerchantId ?? ""
    );
    /** Set when the backend has no receptionist simulator yet (#4709) — a
     *  different thing from a request that failed, and worth saying plainly. */
    const [unavailable, setUnavailable] = useState<boolean>(false);

    const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
    const [companiesLoading, setCompaniesLoading] = useState<boolean>(true);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>(
        persisted?.selectedCompanyId ?? ""
    );
    const [jobs, setJobs] = useState<{ id: string; job_title: string }[]>([]);
    const [jobsLoading, setJobsLoading] = useState<boolean>(false);
    const [selectedJobId, setSelectedJobId] = useState<string>(persisted?.selectedJobId ?? "");
    const [profileName, setProfileName] = useState<string>(persisted?.profileName ?? "");
    // Phone: a whitelisted tester by default; "custom" reveals a free-text field.
    const [phoneChoice, setPhoneChoice] = useState<string>(
        persisted?.phoneChoice ?? WHITELIST_PHONES[0].phone
    );
    const [customPhone, setCustomPhone] = useState<string>(persisted?.customPhone ?? "");

    const [messages, setMessages] = useState<SimMessage[]>(() =>
        (persisted?.messages ?? []).map((m) => ({ ...m, at: new Date(m.at) }))
    );
    const [input, setInput] = useState<string>("");
    const [sending, setSending] = useState<boolean>(false);
    const [flow, setFlow] = useState<SimulateFlowState | undefined>(persisted?.flow);
    const [candidateId, setCandidateId] = useState<string>(persisted?.candidateId ?? "");
    /** Receptionist tab: set once the flow has produced a booking request (#4674). */
    const [bookingId, setBookingId] = useState<string>("");
    const [error, setError] = useState<string | null>(null);

    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);

    const threadRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const idRef = useRef<number>(persisted?.idCounter ?? 0);
    const nextId = () => `m${++idRef.current}`;

    const hasStarted = messages.length > 0;
    const selectedJob = jobs.find((j) => j.id === selectedJobId);
    const selectedMerchant = merchants.find((m) => m.id === selectedMerchantId);
    const companyName = userInfo?.company_name || "OurTeam";
    /** What the conversation is pointed at: a job, or a merchant. Everything
     *  below this line treats the two the same way. */
    const targetId = isRcp ? selectedMerchantId : selectedJobId;
    /** Whose replies these are, for the bubble label and the transcript. */
    const replySender = isRcp ? selectedMerchant?.name || "Receptionist" : "Maya";
    const phone = phoneChoice === "custom" ? customPhone.trim() : phoneChoice;
    const phoneWhitelisted = WHITELIST_PHONES.some((w) => w.phone === phone);

    // #3171: poll the authoritative WhatsApp thread so the async wa_call poller
    // messages (interview thank-you, no-answer skip #3824, retry/exhausted, "phone
    // interview in progress") show up in the panel — they're written to the thread
    // but NOT returned by the synchronous /whatsapp/simulate reply. `since` = the
    // newest created_at we've already merged, so each poll is cheap. Read-only
    // endpoint (BE #3836), admin/super_admin — same gate as the simulator itself.
    const lastSeenAtRef = useRef<string | null>(null);
    const pollFailRef = useRef<number>(0);
    useEffect(() => {
        if (!phone) return;
        // Recruiter: unchanged — the poll waits for the candidate the first turn
        // creates. Receptionist: a turn need not mint an id, so the thread is
        // followed from the first message instead.
        if (isRcp ? !selectedMerchantId || !hasStarted : !candidateId) return;
        pollFailRef.current = 0;
        let cancelled = false;
        let interval: ReturnType<typeof setInterval> | undefined;
        const poll = async () => {
            try {
                const rows = isRcp
                    ? await getReceptionistSimulateThread(
                          selectedMerchantId,
                          phone,
                          lastSeenAtRef.current || undefined
                      )
                    : await getMayaSimulateThread(phone, lastSeenAtRef.current || undefined);
                pollFailRef.current = 0;
                if (cancelled || rows.length === 0) return;
                // Fold the thread into the bubbles already shown; `since` keeps overlap to
                // the first poll. A picture is told apart by its image (#4864).
                setMessages((prev) =>
                    mergeThreadRows(
                        prev,
                        rows.map((row) => ({
                            direction: row.direction,
                            body: row.body,
                            created_at: row.created_at,
                            imageUrl: firstImage(row.media),
                        }))
                    )
                );
                const newest = rows[rows.length - 1]?.created_at;
                if (newest) lastSeenAtRef.current = newest;
            } catch (err) {
                // Stop after repeated failures — e.g. the BE endpoint (#3836) isn't
                // live yet — so we don't 404-spam every interval.
                pollFailRef.current += 1;
                if (pollFailRef.current >= 3 && interval) {
                    clearInterval(interval);
                    interval = undefined;
                }
                catchErrorLog(`[maya-sim] thread poll failed: ${err}`);
            }
        };
        poll();
        interval = setInterval(poll, 4000);
        return () => {
            cancelled = true;
            if (interval) clearInterval(interval);
        };
    }, [candidateId, phone, isRcp, selectedMerchantId, hasStarted]);
    const busy = sending || uploading;
    const canSend =
        !!targetId && !!phone && !busy && !unavailable &&
        (input.trim().length > 0 || !!pendingFile);

    // Load the company list — the simulator is admin-only and spans all workspaces,
    // so the admin picks a company first (#3105).
    useEffect(() => {
        if (isRcp) {
            setCompaniesLoading(false);
            return;
        }
        let cancelled = false;
        setCompaniesLoading(true);
        getAdminCompanyList()
            .then((list) => {
                if (!cancelled) setCompanies(list);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Maya Chat Simulator: failed to load companies: ${err}`);
            })
            .finally(() => {
                if (!cancelled) setCompaniesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isRcp]);

    // The merchant list backs the receptionist picker the way the company list
    // backs the recruiter one. `GET /merchants` is already staff/admin-scoped
    // (ourteam-backend#4670), so this needs no new endpoint.
    useEffect(() => {
        if (!isRcp) return;
        let cancelled = false;
        setMerchantsLoading(true);
        // 100 is the endpoint's ceiling — asking for more is a 422, not a clamp.
        listMerchants({ limit: 100 })
            .then((page) => {
                if (cancelled) return;
                setMerchants(page.merchants.map((m) => ({ id: m.id, name: m.name })));
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Maya Chat Simulator: failed to load merchants: ${err}`);
            })
            .finally(() => {
                if (!cancelled) setMerchantsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isRcp]);

    // Load jobs scoped to the chosen company. The job selection is reset in the
    // company <select> onChange (not here) so a restored session keeps its job.
    useEffect(() => {
        setJobs([]);
        if (isRcp || !selectedCompanyId) {
            setJobsLoading(false);
            return;
        }
        let cancelled = false;
        setJobsLoading(true);
        getAdminCompanyJobs(selectedCompanyId)
            .then((rows) => {
                if (!cancelled) setJobs(rows);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(
                    `Maya Chat Simulator: failed to load jobs for company: ${err}`
                );
            })
            .finally(() => {
                if (!cancelled) setJobsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedCompanyId, isRcp]);

    // Persist the session so a refresh keeps the conversation + setup, until the
    // admin starts a new candidate (which clears it).
    useEffect(() => {
        try {
            const data: PersistedSim = {
                selectedCompanyId,
                selectedJobId,
                selectedMerchantId,
                profileName,
                phoneChoice,
                customPhone,
                messages: messages.map((m) => ({ ...m, at: m.at.toISOString() })),
                flow,
                candidateId,
                idCounter: idRef.current,
            };
            localStorage.setItem(storageKey, JSON.stringify(data));
        } catch {
            /* ignore quota / serialization errors */
        }
    }, [
        storageKey,
        selectedCompanyId,
        selectedJobId,
        selectedMerchantId,
        profileName,
        phoneChoice,
        customPhone,
        messages,
        flow,
        candidateId,
    ]);

    // Auto-scroll to the newest message (and the typing indicator).
    useEffect(() => {
        const el = threadRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, sending, uploading]);

    function resetComposerHeight() {
        const el = inputRef.current;
        if (el) el.style.height = "auto";
    }

    function handleInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        setInput(e.target.value);
        const el = e.target;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }

    function handleAttachClick() {
        fileInputRef.current?.click();
    }

    function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        // Reset the input so re-picking the same file still fires onChange.
        e.target.value = "";
        if (!file) return;
        if (file.size > UPLOAD_MAX_BYTES) {
            setError(`"${file.name}" is too large (max ${formatBytes(UPLOAD_MAX_BYTES)}).`);
            return;
        }
        const okExt = UPLOAD_ACCEPT.split(",").some((ext) =>
            file.name.toLowerCase().endsWith(ext.trim())
        );
        if (!okExt) {
            setError(`"${file.name}" is an unsupported type. Allowed: PDF, DOC, DOCX, JPG, PNG.`);
            return;
        }
        setError(null);
        setPendingFile(file);
    }

    /** Serialize the current conversation to a readable `.txt` and trigger a
     *  browser download — fully client-side (#3104). */
    function handleDownloadTranscript() {
        if (messages.length === 0) return;
        const now = new Date();
        const jobTitle = selectedJob?.job_title || "Unknown job";
        const subject = isRcp ? selectedMerchant?.name || "Unknown merchant" : jobTitle;
        const candidateLabel = `${phone}${
            profileName.trim() ? ` (${profileName.trim()})` : " (Test)"
        }`;

        const lines: string[] = [];
        lines.push(
            `Maya Chat Simulator — ${isRcp ? "receptionist" : "recruiter"} transcript`
        );
        lines.push(
            isRcp
                ? `Merchant: ${subject}   Customer: ${candidateLabel}`
                : `Job: ${jobTitle}   Candidate: ${candidateLabel}`
        );
        lines.push(`Generated: ${fmtDateTime(now)}`);
        if (flow) lines.push(`Flow: ${formatStep(flow.current_step)} (${flow.progress})`);
        lines.push("");
        for (const m of messages) {
            const who =
                m.sender === "candidate"
                    ? isRcp
                        ? "Customer"
                        : "Candidate"
                    : replySender;
            // File messages render as "[file] name" (+ caption if any).
            const raw = m.file
                ? `[file] ${m.file.name}${m.body ? ` (${m.body})` : ""}`
                : m.imageUrl
                  ? `[image] ${m.body}`.trimEnd()
                  : m.body;
            // Indent continuation lines so multi-line bodies stay readable.
            const body = raw.replace(/\n/g, "\n           ");
            lines.push(`[${fmtTime(m.at)}]  ${who}: ${body}`);
        }

        const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${isRcp ? "receptionist" : "maya"}-simulator-${slugify(subject)}-${fmtStamp(now)}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    function handleNewCandidate() {
        setMessages([]);
        setFlow(undefined);
        setCandidateId("");
        setBookingId("");
        setUnavailable(false);
        lastSeenAtRef.current = null; // reset the thread-poll cursor (#3171)
        setInput("");
        setPendingFile(null);
        setUploading(false);
        setError(null);
        resetComposerHeight();
        try {
            localStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
        // Keep the selected job/merchant + profile name + phone so the admin can
        // quickly re-run — that is what makes this a refresh rather than a reset.
    }

    /** Apply a simulate/upload response: update candidate id, flow, and Maya bubbles. */
    function applyMayaResponse(resp: {
        replies: string[];
        /** Receptionist only: the replies with their images (#4851). */
        replyItems?: ReceptionistReplyItem[];
        /** Candidate on the recruiter tab, conversation on the receptionist one. */
        candidate_id: string;
        booking_id?: string;
        flow?: SimulateFlowState;
    }) {
        if (resp.candidate_id) setCandidateId(resp.candidate_id);
        if (resp.booking_id) setBookingId(resp.booking_id);
        // Each turn's flow is authoritative — clear the panel when the BE returns
        // no active flow (e.g. after a reroute to a non-chat-native job, #3122);
        // otherwise the origin job's step would stay stale.
        setFlow(resp.flow ?? undefined);
        const items: ReceptionistReplyItem[] =
            resp.replyItems && resp.replyItems.length > 0
                ? resp.replyItems
                : (resp.replies.length > 0 ? resp.replies : ["(no reply)"]).map((body) => ({ body }));
        const mayaMsgs: SimMessage[] = items.map((r) => ({
            id: nextId(),
            sender: "maya",
            body: r.body,
            at: new Date(),
            ...(r.imageUrl ? { imageUrl: r.imageUrl } : {}),
        }));
        setMessages((prev) => [...prev, ...mayaMsgs]);
    }

    async function handleSend() {
        // A pending file takes priority — send it as a multipart upload (#3113).
        if (pendingFile) {
            await handleSendFile();
            return;
        }
        const text = input.trim();
        if (!text || !targetId || sending) return;

        const isFirst = messages.length === 0;
        // The first message carries the entry tag, which is what routes it: the
        // job ref onboards a candidate, and `[RCP:<slug>]` is how
        // `dispatch.claims()` hands a customer to a merchant while the platform
        // number is shared (ourteam-backend#4666).
        const tag = isRcp ? `[RCP:${selectedMerchantId}]` : `[REF:${selectedJobId}]`;
        const bodyToSend = isFirst ? `${text} ${tag}`.trim() : text;

        setMessages((prev) => [
            ...prev,
            { id: nextId(), sender: "candidate", body: text, at: new Date() },
        ]);
        setInput("");
        setError(null);
        setSending(true);
        resetComposerHeight();

        try {
            if (isRcp) {
                const resp = await simulateReceptionistChat({
                    merchant_id: selectedMerchantId,
                    phone,
                    body: bodyToSend,
                    profile_name: profileName.trim() || undefined,
                });
                applyMayaResponse({
                    replies: resp.replies,
                    replyItems: resp.replyItems,
                    candidate_id: resp.conversationId,
                    booking_id: resp.bookingId,
                    flow: resp.flow,
                });
            } else {
                const resp = await simulateMayaChat({
                    phone,
                    body: bodyToSend,
                    profile_name: profileName.trim() || undefined,
                });
                applyMayaResponse(resp);
            }
        } catch (err) {
            if (isRcp && isSimulatorUnavailable(err)) setUnavailable(true);
            else
                setError(
                    err instanceof Error
                        ? err.message
                        : `Couldn't reach ${isRcp ? "the receptionist" : "Maya"}. Please try again.`
                );
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    }

    /** Send the attached file as a multipart upload to the documents step (#3113). */
    async function handleSendFile() {
        if (!pendingFile || uploading) return;
        const file = pendingFile;
        const caption = input.trim();

        setMessages((prev) => [
            ...prev,
            {
                id: nextId(),
                sender: "candidate",
                body: caption,
                at: new Date(),
                file: { name: file.name },
            },
        ]);
        setPendingFile(null);
        setInput("");
        setError(null);
        setUploading(true);
        resetComposerHeight();

        try {
            if (isRcp) {
                const resp = await simulateReceptionistUpload({
                    merchant_id: selectedMerchantId,
                    phone,
                    file,
                    caption: caption || undefined,
                });
                applyMayaResponse({
                    replies: resp.replies,
                    replyItems: resp.replyItems,
                    candidate_id: resp.conversationId,
                    booking_id: resp.bookingId,
                    flow: resp.flow,
                });
            } else {
                const resp = await simulateMayaUpload({
                    phone,
                    file,
                    caption: caption || undefined,
                });
                applyMayaResponse(resp);
            }
        } catch (err) {
            if (isRcp && isSimulatorUnavailable(err)) setUnavailable(true);
            else
                setError(
                    err instanceof Error
                        ? err.message
                        : "Couldn't upload the file. Please try again."
                );
        } finally {
            setUploading(false);
            inputRef.current?.focus();
        }
    }

    return (
            <div className="space-y-6">
                {/* Always on screen, disabled until there is something to act on.
                    They used to appear only once a conversation had started,
                    which made a freshly opened tab look like it was missing the
                    controls the other one had. */}
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="outline"
                        onClick={handleDownloadTranscript}
                        disabled={!hasStarted}
                        title={
                            hasStarted
                                ? "Download this conversation as a .txt"
                                : "Send a message first — there is nothing to download yet"
                        }
                    >
                        <DownloadIcon className="w-4 h-4" />
                        Download transcript
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleNewCandidate}
                        disabled={!hasStarted}
                        title={
                            hasStarted
                                ? `Clear the thread and start again, keeping the ${isRcp ? "merchant" : "company and job"}, name and phone`
                                : "Nothing to refresh yet"
                        }
                    >
                        <RotateCcwIcon className="w-4 h-4" />
                        {isRcp ? "Refresh customer" : "Refresh candidate"}
                    </Button>
                </div>

                {unavailable && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <span className="font-medium">
                            This environment has no receptionist simulator yet.
                        </span>{" "}
                        The endpoint is specified in ourteam-backend#4709 and required by the
                        epic&apos;s isolation contract (#4666, point 8). The recruiter tab is
                        unaffected.
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
                    {/* Chat frame */}
                    <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white flex flex-col h-[calc(100vh-13rem)] min-h-[520px]">
                        {/* WhatsApp-style contact header */}
                        <div className="flex items-center gap-3 px-4 py-3 bg-[#075e54] text-white">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                <BotIcon className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold leading-tight truncate">
                                    {isRcp
                                        ? selectedMerchant
                                            ? selectedMerchant.name
                                            : "AI Receptionist"
                                        : `Maya · ${companyName}`}
                                </div>
                                <div className="text-xs text-white/70 truncate">
                                    {isRcp
                                        ? selectedMerchant
                                            ? "AI Receptionist"
                                            : "Select a merchant to begin"
                                        : selectedJob
                                          ? selectedJob.job_title
                                          : "Select a job to begin"}{" "}
                                    · {phone}
                                </div>
                            </div>
                        </div>

                        {/* Thread */}
                        <div
                            ref={threadRef}
                            className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-[#efeae2]"
                        >
                            {messages.length === 0 ? (
                                <div className="h-full flex items-center justify-center">
                                    <EmptyState
                                        icon={
                                            <MessageSquareIcon className="h-12 w-12 text-gray-400" />
                                        }
                                        title="No messages yet"
                                        description={
                                            targetId
                                                ? isRcp
                                                    ? "Type a message below and hit Send. Your first message automatically carries the merchant's entry tag, the same one a customer's wa.me link sends."
                                                    : "Type a message below and hit Send. Your first message automatically includes the job reference so Maya can start the flow."
                                                : isRcp
                                                  ? "Pick a merchant in the panel, then send your first message to start the conversation."
                                                  : "Pick a job in the panel, then send your first message to start the conversation."
                                        }
                                    />
                                </div>
                            ) : (
                                messages.map((m) => (
                                    <ChatBubble key={m.id} msg={m} senderName={replySender} />
                                ))
                            )}
                            {busy && <TypingIndicator />}
                        </div>

                        {/* Error banner */}
                        {error && (
                            <div className="px-4 py-2 bg-rose-50 border-t border-rose-200 text-xs text-rose-700 flex items-center justify-between gap-2">
                                <span>{error}</span>
                                <button
                                    onClick={() => setError(null)}
                                    className="text-rose-400 hover:text-rose-600 flex-shrink-0"
                                    aria-label="Dismiss error"
                                >
                                    <XIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {/* Pending file chip (before send) */}
                        {pendingFile && (
                            <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 text-xs text-blue-700 flex items-center justify-between gap-2">
                                <span className="inline-flex items-center gap-1.5 min-w-0">
                                    <FileTextIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span className="truncate">{pendingFile.name}</span>
                                    <span className="text-blue-400 flex-shrink-0">
                                        ({formatBytes(pendingFile.size)})
                                    </span>
                                </span>
                                <button
                                    onClick={() => setPendingFile(null)}
                                    disabled={uploading}
                                    className="text-blue-400 hover:text-blue-600 flex-shrink-0 disabled:opacity-40"
                                    aria-label="Remove file"
                                >
                                    <XIcon className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}

                        {/* Composer */}
                        <div className="flex items-end gap-2 px-3 py-3 bg-white border-t border-gray-200">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={UPLOAD_ACCEPT}
                                className="hidden"
                                onChange={handleFileSelected}
                            />
                            <button
                                type="button"
                                onClick={handleAttachClick}
                                disabled={!hasStarted || busy}
                                title={
                                    hasStarted
                                        ? "Attach a file (PDF, DOC, DOCX, JPG, PNG)"
                                        : "Start the conversation before attaching a file"
                                }
                                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <PaperclipIcon className="w-5 h-5" />
                            </button>
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                rows={1}
                                disabled={!targetId || uploading || unavailable}
                                placeholder={
                                    !targetId
                                        ? isRcp
                                            ? "Select a merchant to start"
                                            : "Select a job to start"
                                        : pendingFile
                                          ? "Add a caption (optional)…"
                                          : "Type a message…"
                                }
                                className="flex-1 resize-none max-h-32 rounded-2xl border border-gray-300 px-3.5 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                            />
                            <Button
                                onClick={handleSend}
                                disabled={!canSend}
                                className="flex-shrink-0 rounded-full w-10 h-10 p-0"
                                aria-label={pendingFile ? "Send file" : "Send message"}
                            >
                                {busy ? (
                                    <Loader2Icon className="w-4 h-4 animate-spin" />
                                ) : (
                                    <SendIcon className="w-4 h-4" />
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Setup + flow panel */}
                    <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
                            <h2 className="text-sm font-semibold text-gray-900">
                                {isRcp ? "Simulated customer" : "Simulated candidate"}
                            </h2>

                            {isRcp && (
                                <div>
                                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                        Merchant
                                    </label>
                                    <select
                                        value={selectedMerchantId}
                                        onChange={(e) => setSelectedMerchantId(e.target.value)}
                                        disabled={hasStarted || merchantsLoading}
                                        className="w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">
                                            {merchantsLoading
                                                ? "Loading merchants…"
                                                : merchants.length === 0
                                                  ? "No receptionist merchants"
                                                  : "Select a merchant…"}
                                        </option>
                                        {merchants.map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {hasStarted
                                            ? "Refresh the customer to change the merchant."
                                            : "Every company with a receptionist workspace. The customer arrives tagged to this one."}
                                    </p>
                                </div>
                            )}

                            {!isRcp && (
                            <>
                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                    Company
                                </label>
                                <select
                                    value={selectedCompanyId}
                                    onChange={(e) => {
                                        setSelectedCompanyId(e.target.value);
                                        setSelectedJobId("");
                                    }}
                                    disabled={hasStarted || companiesLoading}
                                    className="w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">
                                        {companiesLoading ? "Loading companies…" : "Select a company…"}
                                    </option>
                                    {companies.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                    Job to apply to
                                </label>
                                <select
                                    value={selectedJobId}
                                    onChange={(e) => setSelectedJobId(e.target.value)}
                                    disabled={hasStarted || !selectedCompanyId || jobsLoading}
                                    className="w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">
                                        {!selectedCompanyId
                                            ? "Select a company first"
                                            : jobsLoading
                                              ? "Loading jobs…"
                                              : jobs.length === 0
                                                ? "No published jobs"
                                                : "Select a job…"}
                                    </option>
                                    {jobs.map((j) => (
                                        <option key={j.id} value={j.id}>
                                            {j.job_title}
                                        </option>
                                    ))}
                                </select>
                                {hasStarted && (
                                    <p className="text-xs text-gray-400 mt-1">
                                        Refresh the candidate to change the company or job.
                                    </p>
                                )}
                            </div>
                            </>
                            )}

                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                    Profile name{" "}
                                    <span className="normal-case text-gray-400">(optional)</span>
                                </label>
                                <input
                                    value={profileName}
                                    onChange={(e) => setProfileName(e.target.value)}
                                    disabled={hasStarted}
                                    placeholder={isRcp ? "Test Customer" : "Test Candidate"}
                                    className="w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1 block">
                                    Test phone
                                </label>
                                <select
                                    value={phoneChoice}
                                    onChange={(e) => setPhoneChoice(e.target.value)}
                                    disabled={hasStarted}
                                    className="w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                >
                                    {WHITELIST_PHONES.map((w) => (
                                        <option key={w.phone} value={w.phone}>
                                            {w.label} ({w.phone})
                                        </option>
                                    ))}
                                    <option value="custom">Custom number…</option>
                                </select>
                                {phoneChoice === "custom" && (
                                    <input
                                        value={customPhone}
                                        onChange={(e) => setCustomPhone(e.target.value)}
                                        disabled={hasStarted}
                                        placeholder="+60…"
                                        className="mt-2 w-full h-9 rounded-md border border-gray-300 px-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                    />
                                )}
                                {phone && !phoneWhitelisted ? (
                                    <p className="mt-1 flex items-start gap-1 text-xs text-amber-600">
                                        <AlertTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                                        <span>
                                            Not a whitelisted number. Double-check it&apos;s correct
                                            before sending.
                                        </span>
                                    </p>
                                ) : (
                                    <p className="text-xs text-gray-400 mt-1">
                                        Pick a whitelisted tester or enter a custom number. A different
                                        number starts a brand-new {isRcp ? "customer" : "candidate"}.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-3">
                            <h2 className="text-sm font-semibold text-gray-900">Flow state</h2>
                            {flow ? (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-gray-500">Current step</span>
                                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
                                            {formatStep(flow.current_step)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-gray-500">Progress</span>
                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                            {flow.progress}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400">
                                    {hasStarted
                                        ? `No active ${isRcp ? "booking" : "chat"} flow for this ${isRcp ? "customer" : "candidate"}.`
                                        : `Flow progress appears here once ${isRcp ? "the receptionist" : "Maya"} responds.`}
                                </p>
                            )}
                            {candidateId && (
                                <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500">
                                        {isRcp ? "Conversation ID" : "Candidate ID"}
                                    </span>
                                    <span
                                        className="text-xs font-mono text-gray-600 truncate max-w-[160px]"
                                        title={candidateId}
                                    >
                                        {candidateId}
                                    </span>
                                </div>
                            )}
                            {bookingId && (
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-gray-500">Booking ID</span>
                                    <span
                                        className="text-xs font-mono text-gray-600 truncate max-w-[160px]"
                                        title={bookingId}
                                    >
                                        {bookingId}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
    );
}


/**
 * The simulator page. One screen, two runtimes: Maya answering a candidate on
 * the recruitment webhook, and the receptionist answering a customer on its own
 * (ourteam-backend#4666). They are separate panels rather than one shared
 * session — `key={tab}` remounts on switch — so a conversation in flight on one
 * tab is never mixed into the other's thread or transcript.
 *
 * The receptionist tab is hidden wherever the product itself is off, the same
 * `isReceptionistEnabled()` gate the receptionist routes use.
 */
export default function MayaChatSimulatorPage() {
    const receptionistAvailable = isReceptionistEnabled();
    const [tab, setTab] = useState<SimMode>("recruiter");
    const active = receptionistAvailable ? tab : "recruiter";

    return (
        <AuthLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Maya Chat Simulator</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {active === "receptionist"
                            ? "Drive a simulated WhatsApp conversation to test the receptionist's replies and a merchant's booking flow, without a real WhatsApp number."
                            : "Drive a simulated WhatsApp conversation to test Maya's replies and the chat-native hiring flow, without a real WhatsApp number."}
                    </p>
                </div>

                {/* House table-tab pattern (icon + label, underlined active), the
                    same one Users and Workspaces use: each tab swaps the whole
                    panel under it, so it is a tab, not a pill filter. */}
                {receptionistAvailable && (
                    <div className="flex border-b border-gray-200">
                        {([
                            { key: "recruiter" as const, label: "Maya Recruiter", icon: BriefcaseIcon },
                            { key: "receptionist" as const, label: "Maya Receptionist", icon: StoreIcon },
                        ]).map((t) => {
                            const Icon = t.icon;
                            const isActive = active === t.key;
                            return (
                                <button
                                    key={t.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => { if (!isActive) setTab(t.key); }}
                                    className={cn(
                                        "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                                        isActive
                                            ? "border-blue-600 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700",
                                    )}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                <SimulatorPanel key={active} mode={active} />
            </div>
        </AuthLayout>
    );
}
